import { ActionsDropdown } from '@app/components/actions-dropdown/ActionsDropdown';
import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { TextField } from '@app/components/form-fields/TextField';
import useAgentTypes from '@app/hooks/agents/useAgentTypes';
import useBrands from '@app/hooks/brands/useBrands';
import useInteractionTypeList from '@app/hooks/interactions/useInteractionTypes';
import useLocationList from '@app/hooks/locations/useLocations';
import type { InteractionListFilters } from '@app/services/interaction.types';
import { type MenuProps } from 'antd';
import { debounce } from 'lodash';
import { useState } from 'react';
import { MdSearch } from 'react-icons/md';

export type InteractionFiltersProps = {
    onChange: (values: InteractionListFilters) => void;
};

export const InteractionFilters = ({ onChange }: InteractionFiltersProps) => {
    const { data: interactionTypeList } = useInteractionTypeList({ per_page: 99999 });
    const { data: agentTypeList } = useAgentTypes({ per_page: 99999 });
    const { data: brandList } = useBrands({ per_page: 99999 });
    const { data: locationList } = useLocationList({ per_page: 99999 });

    const [filterSelection, setFilterSelection] = useState<InteractionListFilters>({
        ai_agent_type_id: [],
        brand_id: [],
        call_duration: [],
        email_captured: [],
        forwarded: [],
        fs_booking: [],
        lettings_viewing: [],
        location_id: [],
        mortgage_opportunity: [],
        sales_viewing: [],
        search: '',
        sell_opportunity: [],
        type_id: [],
        valuation_booked: [],
    });

    const updateFilterSelection = (type: keyof typeof filterSelection, value: string[]) => {
        let newSelection: InteractionListFilters;
        if (value.includes('reset')) {
            newSelection = { ...filterSelection, [type]: [] };
        } else {
            newSelection = { ...filterSelection, [type]: value };
            //Clear call related fields if interactions doesnt include calls
            const callTypeId = interactionTypeList?.find((item) => item.name === 'Call')?.id;
            if (
                type === 'type_id' &&
                value.length &&
                callTypeId &&
                !newSelection?.type_id?.includes(callTypeId)
            ) {
                newSelection = { ...newSelection, call_duration: [], forwarded: [] };
            }
        }

        setFilterSelection(newSelection);
        onChange(newSelection);
    };

    const generateFilterOptions = (options: MenuProps['items'] = []): MenuProps['items'] => {
        if (!options) {
            return [];
        }

        return [
            ...options,
            { type: 'divider' },
            {
                key: 'reset',
                label: <span className="text-red">Clear</span>,
            },
        ];
    };

    const typeOptions =
        interactionTypeList?.map((item) => ({
            key: item.id,
            label: item.name,
        })) || [];

    const agentTypeOptions =
        agentTypeList?.map((item) => ({
            key: item.id,
            label: item.name,
        })) || [];

    const brandOptions =
        brandList?.data?.map((item) => ({
            key: item.id,
            label: item.name,
        })) || [];

    const locationOptions =
        locationList?.data?.flatMap((loc) => {
            const toOption = (item: typeof loc) => ({
                key: item.id,
                label: `${item.brand_name} ${item.name}`,
            });
            const children = loc.children ?? [];
            return children.length ? children.map(toOption) : [toOption(loc)];
        }) || [];

    const yesNoOptions = [
        { key: 'yes', label: 'Yes' },
        { key: 'no', label: 'No' },
    ];

    const callDurationOptions = [
        { key: '0-2', label: '0-2 minutes' },
        { key: '2-5', label: '2-5 minutes' },
        { key: '5-999', label: '5+ minutes' },
    ];

    const filters = [
        {
            id: 'type_id',
            label: 'Interaction Type',
            multiple: true,
            items: generateFilterOptions(typeOptions),
        },
        {
            id: 'ai_agent_type_id',
            label: 'AI Agent Type',
            multiple: true,
            items: generateFilterOptions(agentTypeOptions),
        },
        {
            id: 'brand_id',
            label: 'Brand',
            multiple: true,
            items: generateFilterOptions(brandOptions),
        },
        {
            id: 'location_id',
            label: 'Location',
            multiple: true,
            items: generateFilterOptions(locationOptions),
        },
        {
            id: 'sales_viewing',
            label: 'Sales Viewing Booked',
            multiple: false,
            items: generateFilterOptions(yesNoOptions),
        },
        {
            id: 'valuation_booked',
            label: 'Valuation Booked',
            multiple: false,
            items: generateFilterOptions(yesNoOptions),
        },
        {
            id: 'fs_booking',
            label: 'FS Booked',
            multiple: false,
            items: generateFilterOptions(yesNoOptions),
        },
        {
            id: 'sell_opportunity',
            label: 'Sell Opportunity',
            multiple: false,
            items: generateFilterOptions(yesNoOptions),
        },
        {
            id: 'lettings_viewing',
            label: 'Lettings Viewing',
            multiple: false,
            items: generateFilterOptions(yesNoOptions),
        },
        {
            id: 'mortgage_opportunity',
            label: 'Mortgage Opportunity',
            multiple: false,
            items: generateFilterOptions(yesNoOptions),
        },
        {
            id: 'email_captured',
            label: 'Email Captured',
            multiple: false,
            items: generateFilterOptions(yesNoOptions),
        },
        {
            id: 'call_duration',
            label: 'Call Duration',
            multiple: false,
            items: generateFilterOptions(callDurationOptions),
        },
        {
            id: 'forwarded',
            label: 'Call Forwarded',
            multiple: false,
            items: generateFilterOptions(yesNoOptions),
        },
    ];

    const disableFilter = (filterId: string) => {
        if (filterId === 'call_duration' || filterId === 'forwarded') {
            const callTypeId = interactionTypeList?.find((type) => type.name === 'Call')?.id;
            if (callTypeId) {
                const { type_id } = filterSelection;
                return !!type_id?.length && !type_id?.includes(callTypeId);
            }
        }
    };

    return (
        <div className="form-fields-wrapper">
            <TextField
                placeholder="Search Interactions..."
                prefix={<MdSearch size={20} />}
                onChange={debounce((e) => {
                    const newSelection = { ...filterSelection, search: e.target.value };
                    setFilterSelection(newSelection);
                    onChange(newSelection);
                }, 500)}
            />
            <div className="styled-table-filters">
                <StyledButtonGroup justify="flex-start" wrap>
                    {filters.map((filter) => {
                        const itemId = filter.id as keyof Omit<InteractionListFilters, 'search'>;
                        const value = filterSelection?.[itemId];
                        return (
                            <ActionsDropdown
                                {...filter}
                                key={filter.id}
                                title={
                                    <>
                                        {filter.label}
                                        {value?.length ? <strong> ({value?.length})</strong> : null}
                                    </>
                                }
                                items={filter.items || []}
                                menu={{
                                    selectable: true,
                                    selectedKeys: value,
                                    multiple: filter.multiple,
                                    onSelect: ({ selectedKeys }) =>
                                        updateFilterSelection(itemId, selectedKeys),
                                    onDeselect: ({ selectedKeys }) =>
                                        updateFilterSelection(itemId, selectedKeys),
                                }}
                                buttonProps={{
                                    size: 'small',
                                    shape: 'round',
                                    type: 'default',
                                    color: value?.length ? 'green' : 'default',
                                }}
                                disabled={disableFilter(filter.id)}
                                searchable={filter.id === 'location_id'}
                            />
                        );
                    })}
                </StyledButtonGroup>
            </div>
        </div>
    );
};

export default InteractionFilters;
